import { createContext, PropsWithChildren, useContext } from "react";
import { usePageContentContextViewModel, Notification } from "./viewModels/page_content_context_view_model";
import { Portal } from "@mui/material";

import ProgressBar from "../../components/progress_bar";

/**
 * PageContentContext
 */

type PageContentData = {
  pageLoading: boolean;
  pageSaving: boolean;
  customLoading: boolean;
  customLoadingMessage: string;
  setPageActionBarVisibility: (visibility: boolean) => void;
  setPageLoading: (loading: boolean | ((value: boolean) => boolean)) => void,
  setPageSaving: (loading: boolean | ((value: boolean) => boolean)) => void,
  setCustomLoading: (loading: boolean, message?: string) => void,
  pushNotification: (notification: Notification) => void,
  pushInfoNotification: (title: string, message: string, opts?: { link?: string, isAutoFinish?: boolean }) => void,
  pushWarnNotification: (title: string, message: string, opts?: { link?: string, isAutoFinish?: boolean }) => void,
  pushErrorNotification: (title: string, message: string[] | string, opts?: { link?: string, isAutoFinish?: boolean }) => void,
}

export const PageContentContext = createContext<PageContentData>({
  pageLoading: false,
  pageSaving: false,
  customLoading: false,
  customLoadingMessage: 'Carregando...',
  setPageActionBarVisibility: () => { },
  setPageLoading: () => { },
  setPageSaving: () => { },
  setCustomLoading: () => { },
  pushNotification: () => { },
  pushInfoNotification: () => { },
  pushWarnNotification: () => { },
  pushErrorNotification: () => { },
});

/**
 * PageContentProvider
 */

type PageContentProps = {};

export const PageContentProvider = ({
  children,
}: PropsWithChildren<PageContentProps>) => {
  const viewModel = usePageContentContextViewModel();
  return (
    <PageContentContext.Provider value={{
      pageLoading: viewModel.pageLoading,
      pageSaving: viewModel.pageSaving,
      customLoading: viewModel.customLoading,
      customLoadingMessage: viewModel.customLoadingMessage,
      setPageActionBarVisibility: viewModel.setPageActionBarVisibility,
      setPageLoading: viewModel.setPageLoading,
      setPageSaving: viewModel.setPageSaving,
      setCustomLoading: viewModel.handleCustomLoading,
      pushNotification: viewModel.pushNotification,
      pushInfoNotification: viewModel.pushInfoNotification,
      pushWarnNotification: viewModel.pushWarnNotification,
      pushErrorNotification: viewModel.pushErrorNotification,
    }}>
      <div className={`size-full relative`}>
        {
          viewModel.actionBarVisibility &&
          <div
            id='page-actions-portal'
            className='w-full h-[4rem] relative flex flex-col border-b-[1px] justify-center items-center bg-white border-border-light-gray dark:bg-background-dark dark:border-border-dark-gray md:px-4'
            style={{ display: viewModel.pageLoading ? 'none' : 'flex' }}>
          </div>
        }

        <div className={`size-full pb-[4rem] absolute x-0 y-0 overflow-auto`}>
          <div id="body-container">
            {children}
          </div>
        </div>
        {viewModel.pageLoading &&
          <div className={`size-full absolute x-0 y-0 bg-black/50 flex flex-col items-center justify-center`} style={{ zIndex: 1000 }}>
            <div className="size-full bg-light-mode-2 dark:bg-dark-mode-2 flex flex-col items-center justify-center">
              <div className="flex flex-col gap-8">
                <div className="w-32">
                  <img src="images/mini-logo.png" alt="logo gdol" />
                </div>
                <div className="w-full">
                  <ProgressBar />
                </div>
              </div>
            </div>
          </div>
        }
        {viewModel.pageSaving &&
          <div className="absolute size-full top-0 left-0 flex justify-center items-center" style={{ zIndex: 5000 }}>
            <div className="flex-col w-48 p-4 rounded-lg gap-2 border border-stone-200 dark:border-stone-700 flex justify-center items-center bg-[#FFFFFF] dark:bg-[#393939]">
              <div className="w-24">
                <img src='images/mini-logo.png' alt="logo gdol" />
              </div>
              <div className="w-full">
                <ProgressBar />
                <p className="text-black dark:text-white text-center">Salvando</p>
              </div>
            </div>
          </div>
        }
        {viewModel.customLoading &&
          <Portal>
            <div className="absolute size-full top-0 left-0 flex justify-center items-center bg-black/50" style={{ zIndex: 5000 }}>
              <div className="flex-col w-[25rem] p-4 rounded-lg gap-2 border border-stone-200 dark:border-stone-700 flex justify-center items-center bg-[#FFFFFF] dark:bg-[#393939]">
                <div className="w-24">
                  <img src='images/mini-logo.png' alt="logo gdol" />
                </div>
                <div className="w-full">
                  <ProgressBar />
                  <p className="text-black dark:text-white text-center mt-4">{viewModel.customLoadingMessage ?? "Carregando..."}</p>
                </div>
              </div>
            </div>
          </Portal>
        }
      </div>
    </PageContentContext.Provider>
  );
}

/**
 * usePageLoader
 */

export const usePageLoader = (): PageContentData => {
  return useContext(PageContentContext);
}